import React, { useEffect } from 'react';
import { ShieldCheck, Users, Plane, Building2, Landmark, Clock, Check, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { openEnquiry } from '../components/EnquiryModal';
import { Button } from '../components/ui/button';

const GoldenVisa = () => {
  const { t, formatPrice } = useApp();
  useEffect(() => { window.scrollTo(0, 0); document.title = 'UAE Golden Visa | GOBRA Capital Real Estate'; }, []);

  const tiers = [
    { years: '2', threshold: 750000, title: '2-Year Investor Visa', desc: 'For property investments starting from this value, renewable and family-inclusive.' },
    { years: '10', threshold: 2000000, title: '10-Year Golden Visa', desc: 'The flagship long-term residency for real estate investors and their families.' },
  ];
  const benefits = [
    { icon: Clock, title: 'Long-Term Residency', desc: 'Live in the UAE for up to 10 years, fully renewable, without a national sponsor.' },
    { icon: Users, title: 'Sponsor Your Family', desc: 'Include your spouse, children and domestic staff under a single application.' },
    { icon: Plane, title: 'Freedom to Travel', desc: 'Multiple-entry flexibility and the ability to stay outside the UAE without losing residency.' },
    { icon: Landmark, title: '0% Income Tax', desc: 'Enjoy Dubai\u2019s tax-friendly environment with no personal income tax on your earnings.' },
    { icon: Building2, title: 'Own Freehold Property', desc: 'Hold full freehold ownership of your Dubai property with strong capital appreciation.' },
    { icon: ShieldCheck, title: 'Stability & Security', desc: 'World-class safety, healthcare and infrastructure in one of the world\u2019s safest cities.' },
  ];
  const steps = [
    'Choose a qualifying property with your GOBRA advisor',
    'Complete the purchase and register with the Dubai Land Department',
    'Submit your Golden Visa application with the title deed',
    'Receive medical & Emirates ID appointments',
    'Golden Visa issued \u2013 welcome to the UAE',
  ];

  return (
    <main className="pt-24 md:pt-28">
      <section className="relative h-[50vh] min-h-[380px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Dubai" className="h-full w-full object-cover" />
        <div className="absolute inset-0 gobra-hero-overlay" />
        <div className="absolute inset-0 flex flex-col justify-center max-w-[1400px] mx-auto px-4 md:px-8">
          <span className="text-gobra-orange text-sm font-semibold tracking-[0.25em] mb-3">UAE GOLDEN VISA</span>
          <h1 className="font-title text-4xl md:text-6xl font-bold text-white max-w-2xl">Invest in Dubai. Secure long-term residency.</h1>
          <p className="text-white/85 mt-4 max-w-xl">Owning qualifying real estate in Dubai can make you eligible for the UAE Golden Visa – long-term residency for you and your family.</p>
          <div className="mt-6 inline-flex items-center gap-3 bg-white/95 rounded-full pl-4 pr-5 py-2 w-fit">
            <span className="text-xs font-semibold text-gobra-slate">Golden Visa by</span>
            <img src={`${process.env.PUBLIC_URL || ''}/brand/dvglobal-logo.png`} alt="DV Global" className="h-6 w-auto" />
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="text-center mb-10">
          <div className="h-1 w-12 bg-gobra-terracotta rounded-full mb-3 mx-auto" />
          <h2 className="font-title text-3xl md:text-4xl font-bold text-gobra-slate">Eligibility by Investment</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {tiers.map((tier) => (
            <div key={tier.years} className="bg-white border border-gobra-sand rounded-2xl p-7 card-lift">
              <div className="flex items-baseline gap-2">
                <span className="font-title text-5xl font-bold text-gobra-green">{tier.years}</span>
                <span className="text-gobra-slate font-medium">years</span>
              </div>
              <h3 className="font-title text-xl font-semibold text-gobra-slate mt-3">{tier.title}</h3>
              <p className="text-muted-foreground text-sm mt-2">{tier.desc}</p>
              <div className="mt-4 pt-4 border-t border-gobra-sand">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('from')}</div>
                <div className="font-title text-2xl font-bold text-gobra-terracotta">{formatPrice(tier.threshold)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gobra-sand/60 py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <div className="h-1 w-12 bg-gobra-terracotta rounded-full mb-3 mx-auto" />
            <h2 className="font-title text-3xl md:text-4xl font-bold text-gobra-slate">Why the Golden Visa</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white border border-gobra-sand rounded-xl p-6">
                <span className="h-12 w-12 rounded-xl bg-gobra-green/10 flex items-center justify-center">
                  <b.icon className="h-6 w-6 text-gobra-green" />
                </span>
                <h3 className="font-title text-lg font-semibold text-gobra-slate mt-4">{b.title}</h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-[900px] mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="text-center mb-10">
          <div className="h-1 w-12 bg-gobra-terracotta rounded-full mb-3 mx-auto" />
          <h2 className="font-title text-3xl md:text-4xl font-bold text-gobra-slate">Your Path to the Golden Visa</h2>
        </div>
        <div className="space-y-4">
          {steps.map((s, i) => (
            <div key={s} className="flex items-start gap-4 bg-white border border-gobra-sand rounded-xl p-5">
              <span className="h-9 w-9 shrink-0 rounded-full bg-gobra-green text-white flex items-center justify-center font-title font-semibold">{i + 1}</span>
              <p className="text-gobra-slate pt-1.5">{s}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 gobra-gradient rounded-2xl p-8 text-center">
          <h3 className="font-title text-2xl font-bold text-white">Ready to invest and apply?</h3>
          <p className="text-white/70 mt-2">Our advisors will match you with a qualifying property and guide your Golden Visa application end-to-end.</p>
          <Button onClick={() => openEnquiry('Golden Visa Consultation')}
            className="mt-6 bg-white text-gobra-green hover:bg-gobra-orange hover:text-white rounded-full px-8 py-6 text-base">
            {t('get_in_touch')} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <a href="https://dvglobal.ae" target="_blank" rel="noopener noreferrer"
            className="mt-6 flex items-center justify-center gap-3">
            <span className="text-white/70 text-sm">In partnership with</span>
            <span className="bg-white rounded-lg px-3 py-1.5 inline-flex items-center">
              <img src={`${process.env.PUBLIC_URL || ''}/brand/dvglobal-logo.png`} alt="DV Global" className="h-6 w-auto" />
            </span>
          </a>
          <p className="text-white/50 text-xs mt-4">Golden Visa advisory delivered with DV Global (dvglobal.ae). Thresholds are indicative; final eligibility is set by UAE authorities.</p>
        </div>
      </section>
    </main>
  );
};

export default GoldenVisa;
